import { Component } from "react";

/**
 * ErrorBoundary — red de seguridad institucional contra crashes de render.
 *
 * Por qué existe: cuando un componente lanza durante el render (p.ej.
 * toast.error recibe un objeto de Pydantic v2 → React: "Objects are not
 * valid as a React child"), sin este boundary el árbol entero colapsa y
 * el usuario ve una pantalla en blanco sin ningún feedback.
 *
 * Uso: envuelve <AppRouter> en App.js (ver comentario en ese archivo).
 * El detalle técnico se manda solo a console.error, nunca a la UI.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: "" };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      // Extrae solo el mensaje corto — el stack completo va a consola
      errorMessage: error?.message || "Unknown render error",
    };
  }

  componentDidCatch(error, info) {
    // Log técnico para DevTools / Dozzle — nunca expuesto en UI
    console.error("[ErrorBoundary] Render error caught:", error, info);
  }

  handleReload = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="min-h-screen flex items-center justify-center bg-background p-6"
          role="alert"
          aria-live="assertive"
        >
          <div className="max-w-md w-full rounded-lg border border-destructive/40 bg-destructive/5 p-8 space-y-4 text-center shadow-lg">
            {/* Icon */}
            <div className="flex justify-center">
              <svg
                className="w-12 h-12 text-destructive"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                />
              </svg>
            </div>

            <div className="space-y-1">
              <h1 className="text-xl font-semibold text-foreground">
                Algo salió mal
              </h1>
              <p className="text-sm text-muted-foreground">
                La aplicación encontró un error inesperado. Recarga la página
                para continuar. Si el problema persiste, contacta a soporte.
              </p>
            </div>

            <button
              onClick={this.handleReload}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Volver al inicio
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
